/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-empty-function */
import { Calendar, Col, Row, Select, TagProps } from 'antd';
import { HeaderRender } from 'antd/es/calendar/generateCalendar';
import dayjs, { Dayjs as DayjsDate } from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { StyledDate, StyledTag } from './multi-date-picker.styles';

dayjs.extend(localeData);

type CustomDatePickerProps = {
  onChange?: ((dates: Date[]) => void) | (() => void);
  value?: Date[];
  format?: string;
  setRangeRequired: Dispatch<SetStateAction<boolean>>;
};

interface CustomTagProps extends TagProps {
  value: string;
}

export const MultipleDatePicker = ({
  onChange = () => {},
  value = [],
  format = 'YYYY/DD/MM',
  setRangeRequired,
}: CustomDatePickerProps) => {
  const { t } = useTranslation();

  const toArrayOfStrings = useCallback(
    (value: Date[]) => {
      const arr = Array.isArray(value) ? value : [value];
      if (arr.length > 0) {
        setRangeRequired(true);
      }

      const stringArr = arr.map((date) => dayjs(date).toString());

      return stringArr.filter((e) => e != null && e != undefined);
    },
    [setRangeRequired],
  );

  function toArrayOfDates(value: string[]): Date[] {
    const arr = Array.isArray(value) ? value : [value];
    const stringArr = arr.map((date) => dayjs(date).toDate());

    return stringArr.filter((e) => e != null && e != undefined);
  }

  const currentStringArr = useMemo(
    () => toArrayOfStrings(value),
    [value, toArrayOfStrings],
  );

  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const _onChange = useCallback(
    (selected: DayjsDate) => {
      const index = currentStringArr.findIndex((e: string) => {
        return dayjs(e).isSame(selected, 'day');
      });

      const temp = [...currentStringArr];

      if (index !== -1) {
        temp.splice(index, 1);
      } else {
        temp.push(dayjs(selected).toString());
      }

      onChange(toArrayOfDates(temp));
    },
    [currentStringArr, onChange],
  );

  const customCellRender = useCallback(
    (current: DayjsDate) => {
      if (
        currentStringArr.some((e: string) => current.isSame(dayjs(e), 'day'))
      ) {
        return (
          <StyledDate className={'selectedDate'}>
            {current.format('DD')}
          </StyledDate>
        );
      }

      return <div>{current.format('DD')}</div>;
    },
    [currentStringArr],
  );

  const customHeaderRender: HeaderRender<DayjsDate> = useCallback(
    ({ value: headerValue, onChange: headerOnChange }) => {
      const start = 0;
      const end = 12;
      const monthOptions = [];

      let current = headerValue.clone();
      const localeData = headerValue.localeData();
      const months = [];
      for (let i = 0; i < 12; i++) {
        current = current.month(i);
        months.push(localeData.monthsShort(current));
      }

      for (let i = start; i < end; i++) {
        monthOptions.push(
          <Select.Option key={i} value={i}>
            {months[i]}
          </Select.Option>,
        );
      }
      const month = headerValue.month();

      return (
        <div style={{ padding: 8 }}>
          <Row gutter={8}>
            <Col>
              <Select
                size={'middle'}
                value={month}
                popupMatchSelectWidth={false}
                onMouseDown={onPreventMouseDown}
                onChange={(newMonth) => {
                  const now = headerValue.clone().month(newMonth);
                  headerOnChange(now);
                }}
              >
                {monthOptions}
              </Select>
            </Col>
          </Row>
        </div>
      );
    },
    [],
  );

  const customTagRender = useCallback(
    ({ value: tagValue, onClose }: CustomTagProps) => {
      return (
        <StyledTag
          onClick={onClose}
          onClose={onClose}
          closable
          onMouseDown={onPreventMouseDown}
        >
          {dayjs(tagValue)?.format(format)}
        </StyledTag>
      );
    },
    [format],
  );

  const onDeselect = useCallback(
    (oldSelect: string) => {
      const newVal = currentStringArr.filter(
        (e) => !dayjs(e).isSame(dayjs(oldSelect)),
      );
      onChange(toArrayOfDates(newVal));
    },
    [currentStringArr, onChange],
  );

  return (
    <Select
      mode='tags'
      size='large'
      onDeselect={onDeselect}
      value={currentStringArr}
      tagRender={customTagRender}
      popupMatchSelectWidth={true}
      placeholder={t('fields.modal.placeholders.exceptionDays')}
      dropdownRender={() => {
        return (
          <Calendar
            fullscreen={false}
            onSelect={_onChange}
            headerRender={customHeaderRender}
            fullCellRender={customCellRender}
            defaultValue={dayjs()}
          />
        );
      }}
    />
  );
};
